import React, { useState, useEffect } from "react";
import "../styles/addProduct.css";
import { db, storage, auth } from "../utils/firebaseconfig";
import { collection, addDoc, doc, updateDoc, arrayUnion, getDoc, setDoc } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { v4 } from "uuid";
import { onAuthStateChanged } from "firebase/auth";
import { useNavigate } from "react-router-dom";
import { checkVerification } from "../utils/verificationCheck";

function AddProduct() {
  const [product, setProduct] = useState({
    title: "",
    description: "",
    category: "",
    price: "",
    condition: "new",
    brand: "",
    dimensions: "",
    weight: "",
    countryOfOrigin: "",
    keywords: "",
  });

  const [images, setImages] = useState([]);
  const [invoice, setInvoice] = useState(null);
  const [currentUserID, setCurrentUserID] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [errors, setErrors] = useState({});
  const [isVerified, setIsVerified] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (!user) {
        navigate("/login");
      } else {
        setCurrentUserID(user.uid);
        setIsVerified(user.emailVerified);
        
        // If not verified, show verification warning
        if (!user.emailVerified) {
          alert("Email verification is required to add products. Please verify your email.");
        }
      }
    });
    return () => unsubscribe();
  }, [navigate]);

  const handleChange = (e) => {
    setProduct({ ...product, [e.target.name]: e.target.value });
  };

  const handleFileChange = (e) => {
    const files = e.target.files;
    if (e.target.name === "images") setImages([...files]);
    else if (e.target.name === "invoice") setInvoice(files[0]);
  };

  const validateFields = () => {
    const newErrors = {};

    if (product.title.trim().length < 3) newErrors.title = "Title must be at least 3 characters.";
    if (product.description.trim().length < 10) newErrors.description = "Description must be at least 10 characters.";
    if (!product.price || parseFloat(product.price) <= 0) newErrors.price = "Price must be a positive number.";
    if (!product.brand.trim()) newErrors.brand = "Brand is required.";
    if (!product.weight || parseFloat(product.weight) <= 0) newErrors.weight = "Weight must be a positive number.";
    if (product.dimensions && !/^\d+x\d+x\d+$/.test(product.dimensions)) newErrors.dimensions = "Dimensions must be in format LxWxH (e.g., 10x20x30)";
    if (!product.category) newErrors.category = "Please select a category.";
    if (images.length === 0) newErrors.images = "At least one image is required.";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Check if user is verified
    if (!checkVerification(navigate)) {
      return;
    }
    
    if (!validateFields()) {
      alert("Please correct the errors before submitting.");
      return;
    }

    setIsUploading(true);
    try {
      const uploadedImageUrls = [];
      for (let file of images) {
        const imageRef = ref(storage, `product_images/${v4()}-${file.name}`);
        await uploadBytes(imageRef, file);
        const downloadURL = await getDownloadURL(imageRef);
        uploadedImageUrls.push(downloadURL);
      }

      let invoiceUrl = "";
      if (invoice) {
        const invoiceRef = ref(storage, `product_invoices/${v4()}-${invoice.name}`);
        await uploadBytes(invoiceRef, invoice);
        invoiceUrl = await getDownloadURL(invoiceRef);
      }

      const keywordArray = product.keywords
        ? product.keywords.split(",").map((kw) => kw.trim().toLowerCase())
        : [];

      if (product.category && !keywordArray.includes(product.category.toLowerCase())) {
        keywordArray.push(product.category.toLowerCase());
      }

      const productRef = await addDoc(collection(db, "Product"), {
        ...product,
        keywords: keywordArray,
        ownerID: currentUserID,
        images: uploadedImageUrls,
        invoiceURL: invoiceUrl,
        createdAt: new Date().toISOString(),
        rating: 0,
        relatedPosts: [],
        relatedAuctions: []
      });

      const userDocRef = doc(db, "User", currentUserID);
      const userDocSnap = await getDoc(userDocRef);

      if (!userDocSnap.exists()) {
        await setDoc(userDocRef, {
          products: [productRef.id],
          createdAt: new Date().toISOString()
        });
      } else {
        await updateDoc(userDocRef, {
          products: arrayUnion(productRef.id)
        });
      }

      navigate("/");
    } catch (err) {
      console.error("Error uploading product:", err);
      alert("Something went wrong while uploading.");
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="add-product-container">
      {isUploading && (
        <div className="upload-overlay">
          <div className="spinner"></div>
          <p>Uploading product, please wait...</p>
        </div>
      )}
      <h2 className="form-title">Add a New Product</h2>
      
      {!isVerified && (
        <div className="verification-warning">
          <p><strong>Email verification required!</strong> You need to verify your email before adding products.</p>
          <button 
            onClick={() => checkVerification(navigate)}
            className="verify-email-button"
          >
            Verify Email
          </button>
        </div>
      )}
      
      <form onSubmit={handleSubmit} className="add-product-form">
        {["title", "description", "brand", "dimensions", "weight"].map((field) => (
          <div className="form-group" key={field}>
            <label>{field.charAt(0).toUpperCase() + field.slice(1)}:</label>
            {field === "description" ? (
              <textarea name={field} value={product[field]} onChange={handleChange} />
            ) : (
              <input type={field === "weight" ? "number" : "text"} name={field} value={product[field]} onChange={handleChange} />
            )}
            {errors[field] && <p style={{ color: "red" }}>{errors[field]}</p>}
          </div>
        ))}

        <div className="form-group">
          <label>Category:</label>
          <select name="category" value={product.category} onChange={handleChange}>
            <option value="">Select Category</option>
            {["Watches", "Mobiles", "Laptops", "Beauty", "Cars", "Appliances",
              "Fragrances", "Eyewear", "Gaming", "Televisions", "Men's",
              "Women's", "Kid's", "Sports", "Deals"].map((cat) => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>
          {errors.category && <p style={{ color: "red" }}>{errors.category}</p>}
        </div>

        <div className="form-group">
          <label>Price (AED):</label>
          <input type="number" name="price" value={product.price} onChange={handleChange} />
          {errors.price && <p style={{ color: "red" }}>{errors.price}</p>}
        </div>

        <div className="form-group">
          <label>Condition:</label>
          <select name="condition" value={product.condition} onChange={handleChange}>
            <option value="new">New</option>
            <option value="used">Used</option>
            <option value="refurbished">Refurbished</option>
          </select>
        </div>

        <div className="form-group">
          <label>Product Images (at least 1):</label>
          <input type="file" name="images" accept="image/*" multiple onChange={handleFileChange} />
          {errors.images && <p style={{ color: "red" }}>{errors.images}</p>}
        </div>

        <div className="form-group">
          <label>Keywords (comma separated):</label>
          <input type="text" name="keywords" value={product.keywords} onChange={handleChange} />
        </div>

        <div className="form-group">
          <label>Invoice (optional PDF or image):</label>
          <input type="file" name="invoice" accept=".pdf,image/*" onChange={handleFileChange} />
        </div>

        <button 
          type="submit" 
          className="add-product-button" 
          disabled={!isVerified}
        >
          {isVerified ? "Add Product" : "Verify Email to Add Product"}
        </button>
      </form>
    </div>
  );
}

export default AddProduct;
